import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useTheme,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import useLobby from "@/hooks/use-lobby";
import { LANG } from "@/types";
import {
  CheckCircle,
  AttachMoney,
  Assessment,
  Business,
  Timeline,
} from "@mui/icons-material";

interface Product {
  unitName: string;
  quantity: number;
  unitPrice: number;
}

interface TenderQuotation {
  tender: { title: string; id: string; status: string };
  id: string;
  status: string;
  products: Product[];
  company: {
    CompanyNr: string;
    nameAr: string;
  };
}

interface QuatationsStatisticsProps {
  offerID: string;
}

const QuatationsStatistics: React.FC<QuatationsStatisticsProps> = ({
  offerID,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const { getListtenderQuotationsByOfferId } = useLobby();
  const [quotations, setQuotations] = useState<TenderQuotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Color palette
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    "#4caf50",
    "#ff9800",
    "#f44336",
    "#9c27b0",
  ];

  // Fetch quotations data
  useEffect(() => {
    const fetchQuotations = async () => {
      try {
        setLoading(true);
        setError(null);

        if (offerID) {
          const response = await getListtenderQuotationsByOfferId(offerID);
          if (response && response.data) {
            setQuotations(response.data);
          } else {
            setQuotations([]);
          }
        }
      } catch (error) {
        console.error("Error fetching quotations:", error);
        setError(t("alerts.error.fetching"));
      } finally {
        setLoading(false);
      }
    };

    fetchQuotations();
  }, [offerID]);

  // Calculate statistics
  const totalQuotations = quotations.length;
  const completedQuotations = quotations.filter(
    (q) => q.status === "Completed"
  ).length;
  const pendingQuotations = quotations.filter(
    (q) => q.status === "Pending"
  ).length;
  const averageValue =
    quotations.reduce(
      (acc, q) =>
        acc +
        q.products.reduce(
          (sum, product) => sum + product.unitPrice * product.quantity,
          0
        ),
      0
    ) / (totalQuotations || 1);

  // Prepare data for charts
  const statusCounts = quotations.reduce((acc: Record<string, number>, q) => {
    acc[q.status] = (acc[q.status] || 0) + 1;
    return acc;
  }, {});

  const pieData = Object.keys(statusCounts).map((status, index) => ({
    name: status,
    value: statusCounts[status],
    color: colors[index % colors.length],
  }));

  const topQuotations = quotations
    .map((q) => ({
      title: q.tender.title,
      status: q.status,
      value: q.products.reduce(
        (sum, product) => sum + product.unitPrice * product.quantity,
        0
      ),
      company: q.company.CompanyNr,
      companyName: q.company.nameAr,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const barData = topQuotations.map((q) => ({
    name: q.title.length > 20 ? q.title.substring(0, 20) + "..." : q.title,
    value: q.value,
    company: q.companyName,
  }));

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={4} key={item}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
          {[1, 2].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Skeleton variant="rectangular" height={300} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        direction: locale === LANG.AR ? "rtl" : "ltr",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Assessment />
          {t("statistics.quotationsStatistics")}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t("statistics.ComparisonTitle")}
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
              border: `1px solid ${theme.palette.primary.main}20`,
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <Timeline />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalQuotations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("statistics.totalQuotations")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: `linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)`,
              border: `1px solid #4caf5020`,
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "50%",
                    backgroundColor: "#4caf50",
                    color: "white",
                  }}
                >
                  <CheckCircle />
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, mb: 1, color: "#4caf50" }}
              >
                {completedQuotations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("statistics.completedQuotations")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: `linear-gradient(135deg, #ff980015 0%, #ff980005 100%)`,
              border: `1px solid #ff980020`,
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "50%",
                    backgroundColor: "#ff9800",
                    color: "white",
                  }}
                >
                  <Business />
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, mb: 1, color: "#ff9800" }}
              >
                {pendingQuotations}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("status.waiting")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.secondary.main}05 100%)`,
              border: `1px solid ${theme.palette.secondary.main}20`,
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.secondary.main,
                    color: "white",
                  }}
                >
                  <AttachMoney />
                </Box>
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: theme.palette.secondary.main,
                }}
              >
                {averageValue.toFixed(0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("statistics.averageValue")} ({t("currency")})
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("statistics.quotationsByStatus")}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t("statistics.topQuotationsByValue")}
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Quotations Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            {t("statistics.topQuotationsByValue")}
          </Typography>

          {topQuotations.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {t("general.noDataMatchingCriteria")}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("tenders.name")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("orders.fields.status")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("tender.fields.value")} ({t("currency")})
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("checkout.fields.company")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topQuotations.map((quotation, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {quotation.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={quotation.status}
                          color={getStatusColor(quotation.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {quotation.value.toFixed(2)} {t("currency")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {quotation.company}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuatationsStatistics;
